import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import type { ApolloQueryResult } from '@apollo/client';
import { GET_HANGING_PROTOCOL_MATCH } from 'modules/Apollo/queries';
import type {
  GetHangingProtocolMatchQuery,
  GetHangingProtocolMatchQueryVariables,
} from 'generated/graphql';
import type { WithApolloContext } from 'modules/Apollo/types';

export type UseHangingProtocolMatchProps = {
  worklistId: Nullable<string>;
  studyIds: ReadonlyArray<string>;
  skippedStudyIds: ReadonlyArray<string>;
  includeAutoloadedPriors: boolean;
} & WithApolloContext;

export type UseHangingProtocolMatchResult = {
  hangingProtocol: GetHangingProtocolMatchQuery['matchHangingProtocolByWorklistsAndStudies'];
  loading: boolean;
  refetch: () => Promise<ApolloQueryResult<GetHangingProtocolMatchQuery>>;
};

/**
 * Standalone hanging-protocol match query. Extracted out of GBVD so that
 * adding/removing priors fires only this small query (plus a per-study
 * fetch via `useManualPriorStudies`) instead of re-running GBVD's massive
 * response.
 *
 * The HP response is small (a layout template + viewport configurations),
 * so its cache write does not block the main thread meaningfully even when
 * it re-runs on every prior change.
 */
export const useHangingProtocolMatch = ({
  worklistId,
  studyIds,
  skippedStudyIds,
  includeAutoloadedPriors,
  context,
}: UseHangingProtocolMatchProps): UseHangingProtocolMatchResult => {
  const variables = useMemo<GetHangingProtocolMatchQueryVariables>(
    () => ({
      worklistIds: worklistId != null ? [worklistId] : [],
      studyIds: studyIds as string[],
      skippedStudyIds: skippedStudyIds as string[],
      includeAutoloadedPriors,
    }),
    [worklistId, studyIds, skippedStudyIds, includeAutoloadedPriors]
  );

  const { data, loading, refetch } = useQuery<
    GetHangingProtocolMatchQuery,
    GetHangingProtocolMatchQueryVariables
  >(GET_HANGING_PROTOCOL_MATCH, {
    variables,
    skip: worklistId == null,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    context: {
      ...context,
      telemetry: {
        ...context?.telemetry,
        query: 'getHangingProtocolMatch',
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
      },
    },
  });

  return {
    hangingProtocol: data?.matchHangingProtocolByWorklistsAndStudies,
    loading,
    refetch,
  };
};
